import { Link } from 'react-router-dom';
import {
  DashboardCard,
  PageHeader,
  SectionHeader,
  Badge,
} from '@zafroi/ui-kit';
import {
  ArrowLeft,
  ArrowUpRight,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

/**
 * Engage → Developer → API reference.
 *
 * Tenant-facing documentation for the `/api/v1/engage/bot-api/*` surface.
 * Each endpoint shows the tier it's gated to, a request/response example,
 * a curl snippet, and notes on rate-limits + origin allowlist. The goal
 * is that a developer can decide whether to use our embed widget OR roll
 * their own UI against the same backend, without needing to spelunk
 * through the OpenAPI schema.
 */
export function ChatbotReferencePage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm">
        <Link
          to="/chatbot"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Chatbot
        </Link>
      </div>

      <PageHeader
        title="Chatbot API reference"
        description="The bot-api endpoints behind the ZAFROI Engage chatbot. Use these directly if you want to build a custom chat UI, or use the embed widget at Developer → Embed widget for the turnkey path."
      />

      <SectionHeader
        title="Authentication"
        description="All routes require a Bearer API key. Tier governs what each key can call."
        withDivider
      />
      <DashboardCard>
        <div className="space-y-3 text-sm text-muted-foreground">
          <p>
            Bearer token in the <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12px]">Authorization</code>{' '}
            header. Issue keys from{' '}
            <a
              href="https://zafroi.com/platform/api-clients"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Operations → Developer → Integrations
              <ArrowUpRight className="h-3 w-3" />
            </a>
            . Pick the <strong className="text-foreground">"ZAFROI Engage Chatbot"</strong> template
            to get a PORTAL-tier key scoped to chat endpoints only.
          </p>
          <p>
            <strong className="text-foreground">Tier matrix:</strong>
          </p>
          <ul className="ml-5 list-disc space-y-1 text-[13px]">
            <li>
              <Tier tier="portal" /> — chat endpoints only.{' '}
              <code className="text-[12px]">intro</code>,{' '}
              <code className="text-[12px]">preview</code>.
            </li>
            <li>
              <Tier tier="integration" /> — chat + admin (your server-side tooling).
            </li>
            <li>
              <Tier tier="tenant_admin" /> — same scopes as your logged-in admin user.
            </li>
          </ul>
        </div>
      </DashboardCard>

      <SectionHeader
        title="Endpoints"
        description={`Base URL: ${apiBase()}`}
        withDivider
      />

      <EndpointCard
        method="GET"
        path="/api/v1/engage/bot-api/intro"
        tier="portal"
        summary="Opening menu shown when the chat panel first opens."
        description="Returns a welcome message (if your KB has an APPROVED entry with category='welcome') and a set of suggested topic chips drawn from your KB. Use this to render quick-pick buttons before the user types."
        response={`{
  "welcome": "Hi! I can help with orders, returns, and policies.",
  "suggestions": [
    "Track an order",
    "Return policy",
    "Raise a request"
  ]
}`}
        curl={`curl -H "Authorization: Bearer $KEY" \\
  ${apiBase()}/api/v1/engage/bot-api/intro`}
      />

      <EndpointCard
        method="POST"
        path="/api/v1/engage/bot-api/preview"
        tier="portal"
        summary="Send a customer message; get the bot's reply."
        description="The core chat endpoint. Constrained-RAG: the bot answers only from APPROVED knowledge-base entries (or whitelisted order lookups when customerId is set). Anything ungrounded returns a polite handoff message."
        request={`{
  "message": "How long do refunds take?",
  "customerId": "optional-uuid-of-the-customer"
}`}
        response={`{
  "reply": "Refunds are processed within 5–7 business days...",
  "grounded": true,
  "handoff": false,
  "sources": [{ "knowledgeId": "uuid", "title": "Refund policy" }],
  "requestRaised": null,
  "suggestions": ["Track an order"]
}`}
        curl={`curl -X POST -H "Authorization: Bearer $KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"message":"How long do refunds take?"}' \\
  ${apiBase()}/api/v1/engage/bot-api/preview`}
        notes="Rate-limited to 60 req/minute per API key. Returns 429 when exceeded."
      />

      <EndpointCard
        method="GET"
        path="/api/v1/engage/bot-api/conversation?customerId=…"
        tier="tenant_admin"
        summary="Read a specific customer's recent bot-chat history."
        description="Admin-only. Used by Engage's Chatbot dashboard to surface what customers have been asking. PORTAL keys cannot call this — privacy guard."
        response={`[
  {
    "id": "uuid",
    "role": "user" | "bot",
    "text": "…",
    "createdAt": "2026-05-25T03:45:00.000Z"
  }
]`}
        curl={`curl -H "Authorization: Bearer $KEY" \\
  "${apiBase()}/api/v1/engage/bot-api/conversation?customerId=…&limit=50"`}
      />

      <EndpointCard
        method="GET"
        path="/api/v1/engage/bot-api/knowledge"
        tier="tenant_admin"
        summary="List your KB entries (drafts + approved)."
        description="Authoring surface. POST / PATCH / approve / DELETE variants exist alongside; see /api/docs for the full CRUD shape."
      />

      <EndpointCard
        method="GET"
        path="/api/v1/engage/bot-api/gaps"
        tier="tenant_admin"
        summary="Topics the bot couldn't answer — pick the ones to author KB for."
      />

      <EndpointCard
        method="GET"
        path="/api/v1/engage/bot-api/usage"
        tier="tenant_admin"
        summary="Your bot-plan tier + month-to-date turn usage."
      />

      <SectionHeader
        title="CORS allowlist"
        description="The widget runs in customers' browsers. The runtime allowlists origins dynamically from each tenant's Organization.website."
        withDivider
      />
      <DashboardCard icon={ShieldCheck} title="How the allowlist works">
        <ul className="space-y-2 text-[13px] text-muted-foreground">
          <li>
            <strong className="text-foreground">No env edits, no support tickets.</strong> Fill in
            your site URL in any of your Organizations (Operations → Workspace → Organizations)
            and that origin is auto-allowed within ~1 minute.
          </li>
          <li>
            <strong className="text-foreground">Defense-in-depth.</strong> PORTAL keys are also
            checked against the Origin header — a key issued for tenant A can only be used from
            tenant A's registered website, not from tenant B's.
          </li>
          <li>
            <strong className="text-foreground">Server-to-server calls bypass CORS.</strong> curl /
            backend processes don't send an Origin header; those still work with a valid Bearer
            key (tier checks still apply).
          </li>
        </ul>
      </DashboardCard>

      <SectionHeader
        title="Errors"
        description="Every failure follows the runtime's standard envelope."
        withDivider
      />
      <DashboardCard icon={ShieldAlert} title="Error envelope">
        <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[12px] leading-snug">
{`{
  "success": false,
  "error": {
    "code": "Forbidden" | "Unauthorized" | "ValidationFailed" | "TooManyRequests" | ...,
    "message": "Human-readable summary",
    "details": { ... }
  },
  "meta": { "requestId": "uuid", "timestamp": "ISO-8601" }
}`}
        </pre>
        <p className="mt-3 text-[12.5px] text-muted-foreground">
          Common ones: <code className="rounded bg-muted px-1 font-mono">401</code> missing/bad key,{' '}
          <code className="rounded bg-muted px-1 font-mono">403</code> wrong tier or origin
          mismatch, <code className="rounded bg-muted px-1 font-mono">429</code> rate limit.
        </p>
      </DashboardCard>
    </div>
  );
}

// ─────────────────────────── helpers ─────────────────────────────────

function apiBase(): string {
  return import.meta.env.VITE_API_BASE_URL ?? 'https://zafroi.com';
}

function Tier({ tier }: { tier: string }) {
  const tones: Record<string, 'primary' | 'accent' | 'neutral' | 'info'> = {
    portal: 'primary',
    integration: 'info',
    tenant_admin: 'accent',
  };
  return (
    <Badge tone={tones[tier] ?? 'neutral'} className="font-mono text-[10.5px]">
      {tier}
    </Badge>
  );
}

function EndpointCard(props: {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  tier: string;
  summary: string;
  description?: string;
  request?: string;
  response?: string;
  curl?: string;
  notes?: string;
}) {
  return (
    <DashboardCard>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 font-mono text-[11px] font-semibold ${methodColor(props.method)}`}
          >
            {props.method}
          </span>
          <code className="font-mono text-[12.5px] text-foreground">{props.path}</code>
          <Tier tier={props.tier} />
        </div>
        <p className="text-[13.5px] text-foreground">{props.summary}</p>
        {props.description && (
          <p className="text-[12.5px] text-muted-foreground">{props.description}</p>
        )}
        {props.request && (
          <details className="rounded-md border border-border bg-muted/20 p-2">
            <summary className="cursor-pointer text-[11.5px] font-medium text-muted-foreground">
              Example request body
            </summary>
            <pre className="mt-2 overflow-x-auto font-mono text-[12px]">{props.request}</pre>
          </details>
        )}
        {props.response && (
          <details className="rounded-md border border-border bg-muted/20 p-2">
            <summary className="cursor-pointer text-[11.5px] font-medium text-muted-foreground">
              Example response
            </summary>
            <pre className="mt-2 overflow-x-auto font-mono text-[12px]">{props.response}</pre>
          </details>
        )}
        {props.curl && (
          <details className="rounded-md border border-border bg-muted/20 p-2">
            <summary className="cursor-pointer text-[11.5px] font-medium text-muted-foreground">
              curl
            </summary>
            <pre className="mt-2 overflow-x-auto font-mono text-[12px]">{props.curl}</pre>
          </details>
        )}
        {props.notes && (
          <p className="text-[11.5px] italic text-muted-foreground">{props.notes}</p>
        )}
      </div>
    </DashboardCard>
  );
}

function methodColor(method: string): string {
  switch (method) {
    case 'GET':
      return 'bg-info/15 text-info';
    case 'POST':
      return 'bg-success/15 text-success';
    case 'PATCH':
      return 'bg-warning/15 text-warning';
    case 'DELETE':
      return 'bg-destructive/15 text-destructive';
    default:
      return 'bg-muted text-muted-foreground';
  }
}
